import { useCallback, useEffect, useState } from 'react';
import type { AccountUser, MeResponse } from '@whaletail/shared';
import { getMe, signOut as apiSignOut } from './api.ts';
import { pauseOnSignOut } from './push.ts';

/** Who is signed in. The session itself lives in an HttpOnly cookie the page cannot read. */
export function useAuth() {
  const [user, setUser] = useState<AccountUser | null>(null);
  const [registrationOpen, setRegistrationOpen] = useState(true);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    getMe()
      .then((r) => {
        setUser(r.user);
        setRegistrationOpen(r.registrationOpen);
      })
      // Offline or server down: stay signed out rather than blocking the map.
      .catch(() => undefined)
      .finally(() => setReady(true));
  }, []);

  const apply = useCallback((r: MeResponse) => {
    setUser(r.user);
    setRegistrationOpen(r.registrationOpen);
  }, []);

  const signOut = useCallback(async () => {
    // Before the session ends (the server needs it to remove the device): a
    // shared browser must not keep getting the previous person's alerts. This
    // only pauses them — signing back in as the same account resumes them
    // (push.ts's resumeAfterSignIn, called from App.tsx).
    await pauseOnSignOut();
    await apiSignOut().catch(() => undefined);
    // The service worker keeps map data for offline use (vite.config.ts). It
    // needs a session to fetch, so do not leave it behind for whoever uses
    // this browser next.
    await globalThis.caches?.delete('whaletail-api').catch(() => undefined);
    setUser(null);
  }, []);

  return { user, ready, registrationOpen, setUser, apply, signOut };
}
